/**
 * Phase 22 iter_173 — Admin → Memberships Center.
 *
 * Consolidates: global toggle · plan editor · artist search + grant/revoke
 * · Founder Candidate list · pending / claimed / expired invitations.
 *
 * SCOPE LOCK: Reuses existing SharedNavigation + shadcn/ui. Never touches
 * Contest Center. Founder plan stays invite_only regardless of the form.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SharedNavigation from "@/components/SharedNavigation";
import api from "@/lib/api";
import { toast } from "sonner";
import { optimizeImage } from "@/lib/mediaUrls";

const STATUS_TONE = {
  pending: "bg-amber-500/20 border-amber-500/40 text-amber-100",
  claimed: "bg-emerald-500/20 border-emerald-500/40 text-emerald-100",
  expired: "bg-white/10 border-white/20 text-gray-300",
  revoked: "bg-rose-500/20 border-rose-500/40 text-rose-100",
};

const AdminMembershipCenter = () => {
  const [enabled, setEnabled] = useState(false);
  const [gatesEnabled, setGatesEnabled] = useState(false);
  const [plans, setPlans] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [invFilter, setInvFilter] = useState("");
  const [artists, setArtists] = useState([]);
  const [q, setQ] = useState("");
  const [savingPlan, setSavingPlan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [gatesBusy, setGatesBusy] = useState(false);
  // iter_223 · read-only membership state snapshot (production diagnostic)
  const [diagBusy, setDiagBusy] = useState(false);
  const [diagData, setDiagData] = useState(null);
  const [diagError, setDiagError] = useState("");
  const [diagFetchedAt, setDiagFetchedAt] = useState("");

  const loadAll = useCallback(async () => {
    setBusy(true);
    try {
      const [tRes, pRes, iRes, gRes] = await Promise.all([
        api.get("/admin/memberships/toggle"),
        api.get("/admin/memberships/plans"),
        api.get("/admin/memberships/invitations"),
        api.get("/admin/tier/gates").catch(() => ({ data: { enabled: false } })),
      ]);
      setEnabled(!!tRes.data?.enabled);
      setPlans(pRes.data?.plans || []);
      setInvitations(iRes.data?.invitations || []);
      setGatesEnabled(!!gRes.data?.enabled);
    } catch {
      toast.error("Failed to load membership center");
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const searchArtists = async () => {
    try {
      const r = await api.get("/admin/memberships/artists", { params: { q } });
      setArtists(r.data?.artists || []);
    } catch { toast.error("Search failed"); }
  };

  const toggleGlobal = async () => {
    try {
      const r = await api.put("/admin/memberships/toggle", { enabled: !enabled });
      setEnabled(!!r.data?.enabled);
      toast.success(r.data?.enabled ? "Memberships enabled — public purchases live" : "Memberships disabled");
    } catch { toast.error("Toggle failed"); }
  };

  const toggleGates = async () => {
    // iter_203 — Premium Feature Enforcement toggle. Flipping ON blocks
    // free users from paid-tier gated features (require_feature). Warn
    // explicitly so this isn't flipped by accident on launch day.
    const next = !gatesEnabled;
    if (next && !window.confirm(
      "Turn ON Premium Feature Enforcement?\n\n"
      + "This will start blocking free users from paid-tier features "
      + "(premium contests, priority uploads, etc). Only do this after "
      + "public purchases are live and QA is complete."
    )) return;
    if (!next && !window.confirm(
      "Turn OFF Premium Feature Enforcement?\n\n"
      + "Free users will regain access to paid-tier features immediately."
    )) return;
    setGatesBusy(true);
    try {
      const r = await api.post("/admin/tier/gates", { enabled: next });
      setGatesEnabled(!!r.data?.enabled);
      toast.success(r.data?.enabled
        ? "🔒 Premium enforcement is now ON — free users are gated."
        : "🔓 Premium enforcement is now OFF — free users bypass gates.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enforcement toggle failed");
    } finally {
      setGatesBusy(false);
    }
  };

  const savePlan = async (plan) => {
    setSavingPlan(plan.plan_id);
    try {
      await api.patch(`/admin/memberships/plans/${plan.plan_id}`, {
        name: plan.name,
        price_usd: Number(plan.price_usd),
        visibility: plan.visibility,
        is_active: plan.is_active,
        benefits: plan.benefits,
        stripe_price_id: plan.stripe_price_id || null,
      });
      toast.success(`Saved ${plan.name}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingPlan(null);
    }
  };

  const grantMembership = async (uid, planId) => {
    if (!window.confirm(`Grant complimentary ${planId} to this artist?`)) return;
    try {
      const r = await api.post("/admin/memberships/grant", {
        user_id: uid, plan_id: planId, complimentary: true,
      });
      if (r.data?.was_new === false) toast.info("Already active — no change");
      else toast.success("Membership granted");
      searchArtists();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Grant failed");
    }
  };

  const revokeMembership = async (membership) => {
    if (!window.confirm(`Revoke ${membership.plan_id} for this artist? Their profile Founder number (if any) stays for audit.`)) return;
    try {
      await api.post(`/admin/memberships/${membership.membership_id}/revoke`);
      toast.success("Revoked");
      searchArtists();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revoke failed");
    }
  };

  const sendInvite = async (uid) => {
    try {
      const r = await api.post("/admin/memberships/invitations", { user_id: uid });
      if (r.data?.was_new === false) toast.info("Invitation already pending");
      else toast.success("🏆 Founder invitation sent");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invite failed");
    }
  };

  const invAction = async (id, action) => {
    if (action !== "resend" && !window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this invitation?`)) return;
    try {
      await api.post(`/admin/memberships/invitations/${id}/${action}`);
      toast.success(`Invitation ${action}d`);
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    }
  };

  // iter_223 · Read-only production membership diagnostic. Uses the
  // admin's existing authenticated session — NO credentials collected,
  // NO writes, NO toggles flipped. Response is displayed for the admin
  // to review and (optionally) copy back to the engineer for analysis.
  const runDiagnostic = async () => {
    setDiagBusy(true);
    setDiagError("");
    setDiagData(null);
    try {
      const r = await api.get("/admin/diagnostics/membership-state");
      setDiagData(r.data);
      setDiagFetchedAt(new Date().toISOString());
      toast.success("Diagnostic loaded — read-only, no changes made");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Diagnostic failed";
      setDiagError(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error("Diagnostic failed");
    } finally {
      setDiagBusy(false);
    }
  };

  const copyDiagnostic = async () => {
    if (!diagData) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(diagData, null, 2));
      toast.success("Copied — paste it back to engineering");
    } catch {
      toast.error("Copy failed — select the text and copy manually");
    }
  };

  const filteredInvs = useMemo(() => {
    if (!invFilter) return invitations;
    return invitations.filter((i) => i.status === invFilter);
  }, [invitations, invFilter]);

  const stats = useMemo(() => {
    const s = { pending: 0, claimed: 0, expired: 0, revoked: 0 };
    for (const i of invitations) s[i.status] = (s[i.status] || 0) + 1;
    return s;
  }, [invitations]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-membership-center">
      <SharedNavigation />
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <Link to="/admin" className="text-xs text-gray-400 hover:text-white">← Back to admin</Link>
            <h1 className="text-2xl sm:text-3xl font-black">💎 Membership Center</h1>
            <p className="text-xs text-gray-500">Plans · Artists · Founding 100 invitations</p>
          </div>
          <div className="flex items-center gap-3">
            <span
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-black border ${
                enabled ? "bg-emerald-500/20 border-emerald-500/40 text-emerald-100" : "bg-white/5 border-white/20 text-gray-300"
              }`}
              data-testid="admin-membership-toggle-state"
            >
              {enabled ? "🟢 Public LIVE" : "⏳ Public OFF"}
            </span>
            <Button
              onClick={toggleGlobal}
              disabled={busy}
              className={enabled ? "bg-rose-500 hover:bg-rose-400 text-white" : "bg-emerald-500 hover:bg-emerald-400 text-black font-black"}
              data-testid="admin-membership-toggle-btn"
            >
              {enabled ? "Turn OFF public" : "Turn ON public"}
            </Button>
          </div>
        </div>

        {/* iter_203 — Launch Controls: two independent switches.
            Enabled → allows public checkout / purchases.
            gates_enabled → enforces premium features (blocks free users). */}
        <section
          className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4"
          data-testid="admin-launch-controls"
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-sm font-black text-white">🚀 Launch Controls</h2>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Two independent switches. Flip Purchases ON first · flip Feature Enforcement ON only when you're ready to block free users.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Purchases toggle (mirrors the header for a full-picture view) */}
            <div
              className={`rounded-xl border p-4 ${
                enabled ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 bg-white/[0.04]"
              }`}
              data-testid="admin-launch-purchases-card"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-white font-black">💳 Public Purchases</p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Controls <code className="text-purple-300">platform_settings.memberships.enabled</code>. When ON, /membership shows real Stripe checkout for paid plans.
                  </p>
                </div>
                <span
                  className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${
                    enabled ? "bg-emerald-500/20 border-emerald-400/60 text-emerald-100" : "bg-white/5 border-white/20 text-gray-300"
                  }`}
                  data-testid="admin-launch-purchases-state"
                >
                  {enabled ? "ON" : "OFF"}
                </span>
              </div>
              <Button
                onClick={toggleGlobal}
                disabled={busy}
                className={`mt-3 w-full ${
                  enabled ? "bg-rose-500 hover:bg-rose-400 text-white" : "bg-emerald-500 hover:bg-emerald-400 text-black font-black"
                }`}
                data-testid="admin-launch-purchases-btn"
              >
                {enabled ? "Turn OFF Purchases" : "Turn ON Purchases"}
              </Button>
            </div>

            {/* Gates enforcement toggle */}
            <div
              className={`rounded-xl border p-4 ${
                gatesEnabled ? "border-rose-500/40 bg-rose-500/10" : "border-white/10 bg-white/[0.04]"
              }`}
              data-testid="admin-launch-gates-card"
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-white font-black">🔒 Premium Feature Enforcement</p>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Controls <code className="text-purple-300">platform_settings.memberships.gates_enabled</code>. When ON, free users are blocked from paid-tier features via <code>require_feature</code>. Flip this on <strong>August 16</strong> only after purchases are live.
                  </p>
                </div>
                <span
                  className={`text-[10px] font-black px-2 py-0.5 rounded-full border ${
                    gatesEnabled ? "bg-rose-500/20 border-rose-400/60 text-rose-100" : "bg-white/5 border-white/20 text-gray-300"
                  }`}
                  data-testid="admin-launch-gates-state"
                >
                  {gatesEnabled ? "ENFORCING" : "OFF"}
                </span>
              </div>
              <Button
                onClick={toggleGates}
                disabled={gatesBusy || busy}
                className={`mt-3 w-full ${
                  gatesEnabled ? "bg-emerald-500 hover:bg-emerald-400 text-black font-black" : "bg-rose-500 hover:bg-rose-400 text-white"
                }`}
                data-testid="admin-launch-gates-btn"
              >
                {gatesBusy
                  ? "Saving…"
                  : gatesEnabled
                  ? "Turn OFF Enforcement"
                  : "Turn ON Enforcement"}
              </Button>
            </div>
          </div>

          {enabled && !gatesEnabled && (
            <p className="text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2"
               data-testid="admin-launch-hint-purchases-on-gates-off">
              💡 Purchases are LIVE but Feature Enforcement is OFF — soft launch. Free users can still access every premium surface. Flip Enforcement ON when you want to gate them.
            </p>
          )}
          {gatesEnabled && !enabled && (
            <p className="text-[11px] text-rose-200 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2"
               data-testid="admin-launch-hint-gates-on-purchases-off">
              ⚠️ Feature Enforcement is ON but Public Purchases are OFF. Free users are blocked from premium features without any way to upgrade. Turn Purchases ON or Enforcement OFF.
            </p>
          )}
        </section>

        {/* iter_223 · TEMPORARY read-only production diagnostic.
            Uses the admin's already-authenticated browser session — no
            credentials collected. Endpoint is strictly read-only:
            reports effective DB name, membership toggles, counts by
            plan, founder-invitation snapshot, and simulated feature
            entitlements for a representative Free and Founding Artist
            user. Never returns Mongo URIs, env vars, or secrets. */}
        <section
          className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-3"
          data-testid="admin-membership-diagnostic"
        >
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h2 className="text-sm font-black text-white">🩺 Membership State Snapshot (read-only)</h2>
              <p className="text-[11px] text-gray-500 mt-0.5">
                Calls <code className="text-purple-300">GET /api/admin/diagnostics/membership-state</code> from your signed-in admin session. Strictly read-only — no toggles are flipped, no records mutated, no secrets exposed. Use this to verify production DB state before flipping any launch switch.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={runDiagnostic}
                disabled={diagBusy}
                className="bg-cyan-500 hover:bg-cyan-400 text-black font-black"
                data-testid="admin-diag-run-btn"
              >
                {diagBusy ? "Running…" : "Run diagnostic"}
              </Button>
              {diagData && (
                <Button
                  onClick={copyDiagnostic}
                  className="bg-white/10 hover:bg-white/20 text-white"
                  data-testid="admin-diag-copy-btn"
                >
                  Copy JSON
                </Button>
              )}
            </div>
          </div>

          {diagError && (
            <p
              className="text-[11px] text-rose-200 bg-rose-500/10 border border-rose-500/30 rounded-lg px-3 py-2"
              data-testid="admin-diag-error"
            >
              {diagError}
            </p>
          )}

          {diagData && (
            <div className="space-y-3" data-testid="admin-diag-result">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
                <div className="rounded border border-white/10 bg-black/40 p-2">
                  <p className="text-gray-500 uppercase font-black">Effective DB</p>
                  <p className="text-white font-mono break-all" data-testid="admin-diag-db-name">
                    {diagData.effective_database_name || "—"}
                  </p>
                </div>
                <div className="rounded border border-white/10 bg-black/40 p-2">
                  <p className="text-gray-500 uppercase font-black">Purchases enabled</p>
                  <p className="text-white font-black" data-testid="admin-diag-enabled">
                    {String(diagData.platform_settings?.enabled)}
                  </p>
                </div>
                <div className="rounded border border-white/10 bg-black/40 p-2">
                  <p className="text-gray-500 uppercase font-black">Gates enabled</p>
                  <p className="text-white font-black" data-testid="admin-diag-gates">
                    {String(diagData.platform_settings?.gates_enabled)}
                  </p>
                </div>
                <div className="rounded border border-white/10 bg-black/40 p-2">
                  <p className="text-gray-500 uppercase font-black">Active memberships</p>
                  <p className="text-white font-black" data-testid="admin-diag-active-count">
                    {diagData.membership_totals?.active_memberships ?? 0}
                  </p>
                </div>
              </div>
              <details className="rounded border border-white/10 bg-black/40" open>
                <summary className="cursor-pointer text-[11px] text-gray-300 px-3 py-2 font-black">
                  Full JSON response · fetched {diagFetchedAt || "just now"}
                </summary>
                <pre
                  className="text-[10px] leading-relaxed text-emerald-200 font-mono p-3 overflow-x-auto max-h-96"
                  data-testid="admin-diag-json"
                >
{JSON.stringify(diagData, null, 2)}
                </pre>
              </details>
              <p className="text-[10px] text-gray-500">
                {diagData.note || "Read-only diagnostic. No mutations of any kind."}
              </p>
            </div>
          )}
        </section>

        {/* Plans */}
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="admin-membership-plans">
          <h2 className="text-sm font-black text-white mb-3">Plans ({plans.length})</h2>
          <div className="space-y-3">
            {plans.map((p, idx) => (
              <div
                key={p.plan_id}
                className="rounded-lg border border-white/10 bg-black/40 p-3 grid grid-cols-1 lg:grid-cols-6 gap-2"
                data-testid={`admin-plan-row-${p.plan_id}`}
              >
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-black">Plan</p>
                  <p className="text-white font-black">{p.name}</p>
                  <p className="text-[10px] text-gray-500 font-mono">{p.plan_id}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-black">Price USD</p>
                  <Input
                    type="number"
                    step="0.01"
                    value={p.price_usd ?? 0}
                    onChange={(e) => setPlans((ps) => ps.map((x, i) => (i === idx ? { ...x, price_usd: e.target.value } : x)))}
                    className="bg-white/5 border-white/10 text-white text-sm"
                    data-testid={`admin-plan-price-${p.plan_id}`}
                  />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-black">Billing</p>
                  <p className="text-white text-sm font-black">{p.billing}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-black">Stripe price_id</p>
                  <Input
                    value={p.stripe_price_id || ""}
                    onChange={(e) => setPlans((ps) => ps.map((x, i) => (i === idx ? { ...x, stripe_price_id: e.target.value } : x)))}
                    placeholder={
                      p.billing === "monthly" || p.billing === "yearly"
                        ? "price_… (required for sub)"
                        : "n/a for founder"
                    }
                    disabled={p.billing !== "monthly" && p.billing !== "yearly"}
                    className="bg-white/5 border-white/10 text-white text-xs font-mono"
                    data-testid={`admin-plan-stripe-price-${p.plan_id}`}
                  />
                </div>
                <div className="lg:col-span-2">
                  <p className="text-[10px] uppercase tracking-widest text-gray-400 font-black">Benefits (one per line)</p>
                  <Textarea
                    value={(p.benefits || []).join("\n")}
                    onChange={(e) => setPlans((ps) => ps.map((x, i) => (i === idx ? { ...x, benefits: e.target.value.split("\n") } : x)))}
                    className="bg-white/5 border-white/10 text-white text-xs h-20"
                    data-testid={`admin-plan-benefits-${p.plan_id}`}
                  />
                </div>
                <div className="lg:col-span-6 flex flex-wrap items-center justify-between gap-2 pt-1">
                  <label className="inline-flex items-center gap-2 text-xs text-gray-300">
                    <input
                      type="checkbox"
                      checked={!!p.is_active}
                      onChange={(e) => setPlans((ps) => ps.map((x, i) => (i === idx ? { ...x, is_active: e.target.checked } : x)))}
                      data-testid={`admin-plan-active-${p.plan_id}`}
                    />
                    Active
                  </label>
                  <label className="inline-flex items-center gap-2 text-xs text-gray-300">
                    <select
                      value={p.visibility}
                      onChange={(e) => setPlans((ps) => ps.map((x, i) => (i === idx ? { ...x, visibility: e.target.value } : x)))}
                      className="bg-white/5 border border-white/10 rounded text-white text-xs h-7 px-1"
                      data-testid={`admin-plan-visibility-${p.plan_id}`}
                    >
                      <option value="public" className="bg-[#13131a]">Public</option>
                      <option value="hidden" className="bg-[#13131a]">Hidden</option>
                    </select>
                    Visibility
                  </label>
                  <Button
                    onClick={() => savePlan(p)}
                    disabled={savingPlan === p.plan_id}
                    className="bg-purple-500 hover:bg-purple-400 text-white h-8 text-xs"
                    data-testid={`admin-plan-save-${p.plan_id}`}
                  >
                    {savingPlan === p.plan_id ? "Saving…" : "Save"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Artist search + grant/invite */}
        <section className="rounded-xl border border-white/10 bg-white/[0.03] p-5" data-testid="admin-membership-artists">
          <h2 className="text-sm font-black text-white mb-3">Artists</h2>
          <div className="flex gap-2 mb-3">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by email or artist name"
              className="bg-white/5 border-white/10 text-white"
              data-testid="admin-membership-search-input"
              onKeyDown={(e) => e.key === "Enter" && searchArtists()}
            />
            <Button onClick={searchArtists} className="bg-white/10 hover:bg-white/20" data-testid="admin-membership-search-btn">
              Search
            </Button>
          </div>
          <div className="space-y-2">
            {artists.map((a) => (
              <div
                key={a.user_id}
                className="rounded-lg border border-white/10 bg-black/40 p-3 flex flex-wrap items-center gap-3"
                data-testid={`admin-artist-row-${a.user_id}`}
              >
                {a.profile_photo ? (
                  <img src={optimizeImage(a.profile_photo, 'avatar')} alt="" className="w-10 h-10 rounded-full object-cover border border-white/10" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-purple-500/30 flex items-center justify-center text-lg">👤</div>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-black text-sm truncate">{a.artist_name || a.name || a.email}</p>
                  <p className="text-[10px] text-gray-500 font-mono truncate">{a.user_id} · {a.email}</p>
                  {a.founder_number ? (
                    <p className="text-[11px] text-amber-300 font-black" data-testid={`admin-artist-founder-${a.user_id}`}>
                      {a.founder_number_label}
                    </p>
                  ) : null}
                  {a.current_membership ? (
                    <p className="text-[11px] text-emerald-300" data-testid={`admin-artist-membership-${a.user_id}`}>
                      Active: {a.current_membership.plan_id} {a.current_membership.lifetime ? "· lifetime" : ""}
                    </p>
                  ) : (
                    <p className="text-[11px] text-gray-500">No active membership</p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <select
                    onChange={(e) => { if (e.target.value) { grantMembership(a.user_id, e.target.value); e.target.value = ""; } }}
                    defaultValue=""
                    className="bg-white/5 border border-white/10 rounded text-white text-xs h-8 px-1"
                    data-testid={`admin-artist-grant-${a.user_id}`}
                  >
                    <option value="" className="bg-[#13131a]">Grant plan…</option>
                    {plans.filter((p) => p.plan_id !== "free").map((p) => (
                      <option key={p.plan_id} value={p.plan_id} className="bg-[#13131a]">{p.name}</option>
                    ))}
                  </select>
                  <Button
                    onClick={() => sendInvite(a.user_id)}
                    disabled={a.founder_number}
                    className="bg-amber-400 hover:bg-amber-300 text-black font-black h-8 text-xs"
                    data-testid={`admin-artist-invite-${a.user_id}`}
                  >
                    🏆 Invite Founder
                  </Button>
                  {a.current_membership && (
                    <Button
                      onClick={() => revokeMembership(a.current_membership)}
                      className="bg-rose-500 hover:bg-rose-400 text-white h-8 text-xs"
                      data-testid={`admin-artist-revoke-${a.user_id}`}
                    >
                      Revoke
                    </Button>
                  )}
                </div>
              </div>
            ))}
            {artists.length === 0 && (
              <p className="text-xs text-gray-500 text-center py-3" data-testid="admin-membership-artists-empty">
                Search for an artist to see plan + Founder options.
              </p>
            )}
          </div>
        </section>

        {/* Invitations */}
        <section className="rounded-xl border border-amber-500/40 bg-amber-500/[0.05] p-5" data-testid="admin-membership-invitations">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-sm font-black text-amber-100">
              🏆 Founding 100 Invitations
            </h2>
            <div className="flex items-center gap-3 text-xs text-amber-100">
              <span data-testid="admin-invitation-stats-pending">Pending: {stats.pending || 0}</span>
              <span data-testid="admin-invitation-stats-claimed">Claimed: {stats.claimed || 0}</span>
              <span data-testid="admin-invitation-stats-expired">Expired: {stats.expired || 0}</span>
              <span data-testid="admin-invitation-stats-revoked">Revoked: {stats.revoked || 0}</span>
            </div>
          </div>
          <div className="flex gap-1 mb-2 flex-wrap">
            {["", "pending", "claimed", "expired", "revoked"].map((s) => (
              <button
                key={s || "all"}
                onClick={() => setInvFilter(s)}
                className={`px-2 py-0.5 rounded-full text-[11px] font-black ${
                  invFilter === s ? "bg-amber-400 text-black" : "bg-white/5 text-gray-300 hover:bg-white/10"
                }`}
                data-testid={`admin-invitation-filter-${s || "all"}`}
              >
                {s ? s : "all"}
              </button>
            ))}
          </div>
          <div className="space-y-2">
            {filteredInvs.map((inv) => (
              <div
                key={inv.invitation_id}
                className="rounded-lg border border-white/10 bg-black/40 p-3 flex flex-wrap items-center gap-2"
                data-testid={`admin-invitation-row-${inv.invitation_id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-black truncate">{inv.user_display || inv.user_email}</p>
                  <p className="text-[10px] text-gray-500 font-mono">{inv.user_id}</p>
                  {inv.founder_number ? (
                    <p className="text-amber-300 text-[11px] font-black">
                      Assigned Founding Artist #{String(inv.founder_number).padStart(3, "0")}
                    </p>
                  ) : (
                    <p className="text-[10px] text-gray-400">Expires {new Date(inv.expires_at).toLocaleDateString()}</p>
                  )}
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-black border ${
                    STATUS_TONE[inv.status] || "bg-white/5 border-white/10"
                  }`}
                  data-testid={`admin-invitation-status-${inv.invitation_id}`}
                >
                  {inv.status}
                </span>
                {inv.status === "pending" && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => invAction(inv.invitation_id, "resend")}
                      className="text-[11px] text-cyan-300 hover:underline"
                      data-testid={`admin-invitation-resend-${inv.invitation_id}`}
                    >
                      Resend
                    </button>
                    <button
                      onClick={() => invAction(inv.invitation_id, "expire")}
                      className="text-[11px] text-gray-300 hover:underline"
                      data-testid={`admin-invitation-expire-${inv.invitation_id}`}
                    >
                      Expire
                    </button>
                    <button
                      onClick={() => invAction(inv.invitation_id, "revoke")}
                      className="text-[11px] text-rose-300 hover:underline"
                      data-testid={`admin-invitation-revoke-${inv.invitation_id}`}
                    >
                      Revoke
                    </button>
                  </div>
                )}
              </div>
            ))}
            {filteredInvs.length === 0 && (
              <p className="text-xs text-gray-500 text-center py-3" data-testid="admin-membership-invitations-empty">
                No invitations {invFilter ? `with status "${invFilter}"` : "yet"}.
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default AdminMembershipCenter;
